import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
    Button,
    Form,
    FormInstance,
    Heading,
    InputPicker,
    Message,
    Schema,
    toaster,
} from "rsuite";
import SignalRTenantFeatureSetting from "components/WorkflowLog/TenantFeature/SignalRTenantFeatureSetting";
import useRefreshPage from "hooks/useRefreshPage";
import useInactiveEnvironment from "hooks/useInactiveEnvironment";
import useInactiveTenant from "hooks/useInactiveTenant";
import constants from "utils/constants";
import {
    ErpCredentials,
    AuthenticationMethod,
    AuthenticationType,
    IErpInfo,
    IErpThrottlingPriority,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import { useParams } from "react-router-dom";
import {
    ITenantFeatureSettingWorkFlow,
    TenantFeatureSettingWorkFlowType,
} from "utils/tenant-workflows/tenant-workflows-types";
import {
    isSettingWorkFlowUpdated,
    updateTenantSettingWorkFlow,
} from "utils/tenant-workflows/tenant-workflows-helper";
import { getTenantFeatureSettingWorkFlow } from "api/environments/tenants/tenant-feature-settings-api";
import usePreviousState from "hooks/usePreviousState";
import {
    loadErpConnectionInfo,
    loadAuthenticationMethods,
    loadErpSecretsInfo,
    loadTenantDefaultKeyVaultCompany,
    loadTenantLinkedThrottlingPriority,
    loadTenantThrottlingPriorities,
    updateErpConnectionInfo,
    updateDefaultKeyVaultCompany,
    updateTenantLinkedThrottlingPriority,
} from "api/environments/tenants/tenant-erp-configurations-api";
import { getErrorMessage } from "api/defaults";
import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";
import { loadCompanies } from "api/environments/tenants/companies-api";
import { Company } from "store/environments/tenants/companies/companies-types";
import "../GeneralConfigurations/GeneralConfigurations.less";
import { cloneDeep, template } from "lodash";
import ResetD365Parameters from "./ResetD365Parameters/ResetD365Parameters";
import ErpConnectionInfoContainer from "./ErpConnectionInfo/ErpConnectionInfoContainer";
import { validate } from "uuid";
import {
    validateIntegrationUser,
    disableIntegrationUserDomainValidation,
} from "api/environments/tenants/users/users-api";
import Axios from "axios";
import useRequestCancellation from "hooks/useRequestCancellation";
import ClientSecretFormGroup from "./ClientSecretFormGroup/ClientSecretFormGroup";
import Footer from "components/Common/Footer/Footer";
import { ItemDataType, TypeAttributes } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

interface ComponentProps {
    envMoniker: string;
    activeEnvironmentId: string;
    tenantMoniker: string;
    activeTenantId: string;
    isFetched: boolean;
    authenticationMethodDescription: string;
    customerType: CustomerType | null;
    showLoader: (isVisible: boolean) => void;
    showError: (errorMessage: string) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    setIsTenantFetched: (isFetched: boolean) => void;
}

const initialErpSecretsInfo = {
    appRegSecretExpiration: {
        timeInDays: null,
        status: null,
        rotationDate: null,
    },
    axSecretExpiration: {
        timeInDays: null,
        status: null,
        rotationDate: null,
    },
};

const emptyDefaultKeyVaultCompanyDropdownItem = {
    id: "",
    name: "",
    remoteId: "",
    disabled: true,
};

const ErpConfigurations: React.FC<ComponentProps> = ({
    envMoniker,
    activeEnvironmentId,
    tenantMoniker,
    activeTenantId,
    isFetched,
    authenticationMethodDescription,
    customerType,
    showLoader,
    showError,
    setIsUnsavedChangeAvailable,
    setIsTenantFetched,
}: ComponentProps) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{ tenantId: string }>();
    const { tenantId } = params;
    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);
    const [
        previousFeatureSettingWorkFlows,
        featureSettingWorkFlows,
        setFeatureSettingWorkFlows,
    ] = usePreviousState<ITenantFeatureSettingWorkFlow[] | null>(null);

    const [authMethods, setAuthMethods] = useState<AuthenticationMethod[]>([]);
    const [erpConnectionInfo, setErpConnectionInfo] =
        useState<ErpCredentials | null>(null);

    const [authMethod, setAuthMethod] = useState<AuthenticationMethod | null>(
        null
    );

    const [isConnectionInfoChanged, setConnectionInfoChanged] =
        useState<boolean>(false);

    const [defaultKeyVaultCompanyId, setDefaultKeyVaultCompanyId] = useState<
        string | null
    >(null);

    const [
        isDefaultKeyVaultCompanyIdFetchedSuccessfully,
        setIsDefaultKeyVaultCompanyIdFetchedSuccessfully,
    ] = useState<boolean | null>(null);

    const [isCompaniesDropdownDisabled, setIsCompaniesDropdownDisabled] =
        useState(true);
    const [companiesData, setCompaniesData] = useState<Record<string, any>[]>(
        []
    );
    const [
        defaultKeyVaultCompanyDropdownItem,
        setDefaultKeyVaultCompanyDropdownItem,
    ] = useState<Company>(emptyDefaultKeyVaultCompanyDropdownItem);

    const [throttlingPriority, setThrottlingPriority] =
        useState<IErpThrottlingPriority | null>(null);
    const [
        isThrottlingPrioritiesFetchedSuccessfully,
        setIsThrottlingPrioritiesFetchedSuccessfully,
    ] = useState<boolean | null>(null);
    const [
        isThrottlingPriorityDropdownDisabled,
        setIsThrottlingPriorityDropdownDisabled,
    ] = useState(true);
    const [throttlingPrioritiesData, setThrottlingPrioritiesData] = useState<
        Record<string, any>[]
    >([]);
    const [throttlingPriorityDropdownItem, setThrottlingPriorityDropdownItem] =
        useState<IErpThrottlingPriority | null>(null);

    const [isIntegrationUserValid, setIsIntegrationUserValid] =
        useState<boolean>(true);
    const [isDismissWarningInProgress, setIsDismissWarningInProgress] =
        useState<boolean>(false);

    const [erpSecretsInfo, setErpSecretsInfo] = useState<IErpInfo>(
        initialErpSecretsInfo
    );

    const keyVaultAppRegistrationSecretRotationWorkflow =
        featureSettingWorkFlows?.find(
            (workflow) =>
                workflow.type ===
                TenantFeatureSettingWorkFlowType.KeyVaultAppRegistrationSecretRotation
        ) ?? null;

    const title = template(t("tenantConfig.d365InformationPage.header"))({
        customerType:
            customerType === CustomerType.AX2012
                ? t("tenantConfig.d365InformationPage.erpType.ax")
                : t("tenantConfig.d365InformationPage.erpType.d365"),
    });

    const showMessage = (message: string, type: TypeAttributes.Status) => {
        let duration;
        switch (type) {
            case "error":
                duration = constants.alertDurationMsec;
                break;
            case "success":
                duration = constants.errorAlertDurationMsec;
                break;
        }

        const options = {
            duration: duration,
        } as ToastContainerProps;

        toaster.push(
            <Message
                showIcon
                type={type}
                style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
            >
                <div>{message}</div>
            </Message>,
            options
        );
    };

    const fetchErpConnectionInfo = async (
        envMoniker: string,
        tenantMoniker: string
    ) => {
        const [payload, statusCode] = await loadErpConnectionInfo(
            envMoniker,
            tenantMoniker
        );
        if (payload && statusCode === constants.statusCode.OK) {
            setErpConnectionInfo(payload);

            const authMethod = {
                authenticationType: payload.clientSecret
                    ? AuthenticationType.ClientSecret
                    : AuthenticationType.UsernamePassword,
                authenticationDescription: authenticationMethodDescription,
            } as AuthenticationMethod;

            setAuthMethod(authMethod);
        } else {
            const errorMessage = getErrorMessage(String(payload), statusCode);
            showMessage(errorMessage, "error");
        }
    };

    const fetchAuthenticationInfo = async (
        envMoniker: string,
        tenantId: string
    ): Promise<void> => {
        return loadAuthenticationMethods(
            envMoniker,
            tenantId,
            cancelTokenSource
        )
            .then(([payload, statusCode]) => {
                if (payload && statusCode === constants.statusCode.OK) {
                    setAuthMethods(payload);
                } else {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        statusCode
                    );
                    showMessage(errorMessage, "error");
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(axiosError)) {
                    showError(error.message);
                }
            });
    };

    const fetchDefaultKeyVaultCompany = async (
        envMoniker: string,
        tenantId: string
    ): Promise<void> => {
        return loadTenantDefaultKeyVaultCompany(
            envMoniker,
            tenantId,
            cancelTokenSource
        )
            .then(([payload, status]) => {
                if (status === constants.statusCode.OK) {
                    setIsDefaultKeyVaultCompanyIdFetchedSuccessfully(true);
                    setDefaultKeyVaultCompanyId(payload?.companyId || "");
                } else {
                    const errorMessage = t(
                        "tenantConfig.d365InformationPage.defaultKeyVaultCompany.fetchValueFailed"
                    );

                    showMessage(errorMessage, "error");

                    setDefaultKeyVaultCompanyId(null);
                    setCompaniesData([]);
                    setIsDefaultKeyVaultCompanyIdFetchedSuccessfully(false);
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(axiosError)) {
                    showError(error.message);
                }
            });
    };

    const fetchTenantFeatureSettingWorkFlow = async (
        envMoniker: string,
        tenantMoniker: string
    ): Promise<void> => {
        return getTenantFeatureSettingWorkFlow(
            envMoniker,
            tenantMoniker,
            TenantFeatureSettingWorkFlowType.KeyVaultAppRegistrationSecretRotation,
            cancelTokenSource
        )
            .then(([workFlow]) => {
                setFeatureSettingWorkFlows(workFlow || []);
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(axiosError)) {
                    showError(error.message);
                }
            });
    };

    const fetchTenantThrottlingPriorities = async (
        envMoniker: string,
        tenantMoniker: string
    ): Promise<void> => {
        if (customerType === CustomerType.AX2012) {
            return;
        }

        if (envMoniker && tenantMoniker) {
            await Promise.all([
                loadTenantThrottlingPriorities(
                    envMoniker,
                    tenantMoniker,
                    cancelTokenSource
                )
                    .then(([payload, statusCode]) => {
                        if (payload && statusCode === constants.statusCode.OK) {
                            setThrottlingPrioritiesData(
                                payload.map((throttlingPriority) => ({
                                    label:
                                        throttlingPriority.priorityName || "",
                                    value: { ...throttlingPriority },
                                }))
                            );
                            if (payload.length) {
                                setIsThrottlingPriorityDropdownDisabled(false);
                            }
                            setIsThrottlingPrioritiesFetchedSuccessfully(true);
                        } else {
                            const errorMessage = t(
                                "tenantConfig.d365InformationPage.throttlingPriority.fetchValueFailed"
                            );

                            showMessage(errorMessage, "error");

                            setThrottlingPrioritiesData([]);
                            setIsThrottlingPriorityDropdownDisabled(true);
                            setIsThrottlingPrioritiesFetchedSuccessfully(false);
                        }
                    })
                    .catch((error: Error) => {
                        const axiosError = error;
                        if (!Axios.isCancel(axiosError)) {
                            showError(error.message);
                        }
                    }),
                loadTenantLinkedThrottlingPriority(
                    envMoniker,
                    tenantMoniker,
                    cancelTokenSource
                )
                    .then(([payload, statusCode]) => {
                        if (payload && statusCode === constants.statusCode.OK) {
                            setThrottlingPriorityDropdownItem(payload);
                        } else if (
                            statusCode === constants.statusCode.NotFound
                        ) {
                            return;
                        } else {
                            const errorMessage = t(
                                "tenantConfig.d365InformationPage.throttlingPriority.fetchSelectedValueFailed"
                            );
                            showMessage(errorMessage, "error");

                            setThrottlingPriorityDropdownItem(null);
                        }
                    })
                    .catch((error: Error) => {
                        const axiosError = error;
                        if (!Axios.isCancel(axiosError)) {
                            showError(error.message);
                        }
                    }),
            ]);
        }
    };

    const fetchIsIntegrationUserDomainValid = async (
        envId: string,
        tenantId: string
    ): Promise<void> => {
        return validateIntegrationUser(envId, tenantId, cancelTokenSource)
            .then(([payload, status]) => {
                if (payload !== null && status === constants.statusCode.OK) {
                    setIsIntegrationUserValid(payload);
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(axiosError)) {
                    showError(error.message);
                }
            });
    };

    const fetchErpSecretsInfo = async (
        envMoniker: string,
        tenantId: string
    ): Promise<void> => {
        return loadErpSecretsInfo(envMoniker, tenantId, cancelTokenSource)
            .then(([payload, status]) => {
                if (payload !== null && status === constants.statusCode.OK) {
                    setErpSecretsInfo(payload);
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(axiosError)) {
                    showError(error.message);
                }
            });
    };

    const fetchData = (
        envMoniker: string,
        envId: string,
        tenantMoniker: string,
        tenantId: string
    ) => {
        if (envMoniker && envId && tenantMoniker && tenantId) {
            showLoader(true);
            Promise.all([
                fetchErpConnectionInfo(envMoniker, tenantId),
                fetchTenantFeatureSettingWorkFlow(envMoniker, tenantMoniker),
                fetchAuthenticationInfo(envMoniker, tenantId),
                fetchDefaultKeyVaultCompany(envMoniker, tenantId),
                fetchTenantThrottlingPriorities(envMoniker, tenantId),
                fetchIsIntegrationUserDomainValid(envId, tenantId),
                fetchErpSecretsInfo(envMoniker, tenantId),
            ]).then(() => {
                showLoader(false);
                setIsTenantFetched(true);
            });
        }
    };

    const formValue = {
        defaultKeyVaultCompany: defaultKeyVaultCompanyDropdownItem,
        throttlingPriority: throttlingPriorityDropdownItem,
        url: erpConnectionInfo?.url,
        authenticationMethod: authMethod?.authenticationType,
        username: erpConnectionInfo?.username,
        clientId: erpConnectionInfo?.clientId,
        secretExpirationDate: erpConnectionInfo?.secretExpirationDate,
        password: erpConnectionInfo?.password,
        clientSecret: erpConnectionInfo?.clientSecret,
    };

    const authClientSecretValidation = (validation: any) => {
        const updatedValidation = cloneDeep(validation);
        if (
            authMethod?.authenticationType === AuthenticationType.ClientSecret
        ) {
            return updatedValidation.isRequired(
                t("generalValidationMessages.isRequired")
            );
        }

        return updatedValidation;
    };

    const authPasswordValidation = (validation: any) => {
        const updatedValidation = cloneDeep(validation);

        if (
            authMethod?.authenticationType ===
            AuthenticationType.UsernamePassword
        ) {
            return updatedValidation.isRequired(
                t("generalValidationMessages.isRequired")
            );
        }

        return updatedValidation;
    };

    const checkCompany = (value: Company) => {
        return value.remoteId !== constants.datCompany;
    };

    const { StringType, DateType, ObjectType } = Schema.Types;
    const formModel = Schema.Model({
        defaultKeyVaultCompany: ObjectType().addRule((value: any) => {
            return checkCompany(value);
        }, t("tenantConfig.d365InformationPage.defaultKeyVaultCompany.notAcceptedCompanyError")),
        url: StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .addRule((value) => {
                return constants.urlRegex.test(value);
            }, t("generalValidationMessages.isUrl")),
        username: StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .isEmail(t("generalValidationMessages.isEmail")),
        clientId: StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .addRule(
                (enteredValue: string) => validate(enteredValue),
                t("generalValidationMessages.isGuid")
            ),
        secretExpirationDate: authClientSecretValidation(DateType()),
        password: authPasswordValidation(StringType()),
        clientSecret: authClientSecretValidation(StringType()),
    });

    useEffect(() => {
        if (
            isSettingWorkFlowUpdated(
                TenantFeatureSettingWorkFlowType.KeyVaultAppRegistrationSecretRotation,
                previousFeatureSettingWorkFlows,
                featureSettingWorkFlows
            )
        ) {
            if (envMoniker && tenantId) {
                fetchErpSecretsInfo(envMoniker, tenantId);
                const message = t(
                    "tenantConfig.d365InformationPage.clientSecret.completedSuccessfully"
                );

                showMessage(message, "success");
            }
        }
    }, [
        envMoniker,
        tenantId,
        previousFeatureSettingWorkFlows,
        featureSettingWorkFlows,
    ]);

    useEffect(() => {
        if (envMoniker && tenantMoniker && activeTenantId && !isFetched) {
            fetchData(
                envMoniker,
                activeEnvironmentId,
                tenantMoniker,
                activeTenantId
            );
        }
    }, [envMoniker, tenantMoniker, activeTenantId, isFetched]);

    useEffect(() => {
        setIsTenantFetched(false);
    }, [setIsTenantFetched]);

    useEffect(() => {
        if (
            envMoniker &&
            tenantId &&
            companiesData.length &&
            defaultKeyVaultCompanyId
        ) {
            setIsCompaniesDropdownDisabled(true);
        }
    });

    useEffect(() => {
        if (
            activeEnvironmentId &&
            activeTenantId &&
            !companiesData.length &&
            defaultKeyVaultCompanyId !== null
        ) {
            loadCompanies(activeEnvironmentId, activeTenantId).then(
                ([payload, statusCode]) => {
                    if (payload && statusCode === constants.statusCode.OK) {
                        setCompaniesData(
                            payload.map((company) => ({
                                label:
                                    `${company.remoteId} / ${company.name}` ||
                                    "",
                                value: { ...company },
                            }))
                        );
                        const selectedDropdownItem = payload.find(
                            (company) =>
                                company.remoteId === defaultKeyVaultCompanyId
                        );
                        if (selectedDropdownItem) {
                            setDefaultKeyVaultCompanyDropdownItem(
                                selectedDropdownItem
                            );
                        } else {
                            setIsCompaniesDropdownDisabled(false);
                        }
                    } else {
                        setIsCompaniesDropdownDisabled(false);
                    }
                }
            );
        }
    }, [
        loadCompanies,
        envMoniker,
        tenantMoniker,
        companiesData,
        defaultKeyVaultCompanyId,
    ]);

    useRefreshPage(() => {
        if (envMoniker && tenantId) {
            setIsTenantFetched(!isFetched);
        }
        setDefaultKeyVaultCompanyId(null);
        setCompaniesData([]);
        setDefaultKeyVaultCompanyDropdownItem(
            emptyDefaultKeyVaultCompanyDropdownItem
        );
        setIsCompaniesDropdownDisabled(true);
        setIsThrottlingPriorityDropdownDisabled(true);
        setConnectionInfoChanged(false);
        if (customerType === CustomerType.D365) {
            fetchTenantThrottlingPriorities(envMoniker, tenantId);
        }
    });

    useInactiveTenant();
    useInactiveEnvironment();

    const checkWorkflowProgressAndUpdateWorkflow = (
        settingWorkflow: ITenantFeatureSettingWorkFlow
    ) => {
        setFeatureSettingWorkFlows((featureSettingWorkFlows) => {
            return updateTenantSettingWorkFlow(
                featureSettingWorkFlows,
                settingWorkflow
            );
        });
    };

    const updateTenantErpConnectionInfo = () => {
        if (isConnectionInfoChanged && envMoniker && tenantId) {
            return updateErpConnectionInfo(
                envMoniker,
                tenantId,
                erpConnectionInfo
            ).then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    setConnectionInfoChanged(false);

                    const type = payload.isValid ? "success" : "error";

                    showMessage(payload.message, type);
                } else {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        status
                    );
                    showMessage(errorMessage, "error");
                }
            });
        }
    };

    const updateTenantDefaultKeyVaultCompany = async () => {
        if (!isDefaultKeyVaultCompanyIdFetchedSuccessfully) {
            return;
        } else if (defaultKeyVaultCompanyId) {
            try {
                const [payload, statusCode] =
                    await updateDefaultKeyVaultCompany(
                        envMoniker,
                        tenantId,
                        defaultKeyVaultCompanyId
                    );
                if (statusCode != constants.statusCode.NoContent) {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        statusCode
                    );
                    showMessage(errorMessage, "error");
                }
            } catch (error) {
                const errorMessage = `${t(
                    "tenantConfig.d365InformationPage.defaultKeyVaultCompany.defaultKeyVaultCompanyUpdateFailed"
                )}.\n${error}`;

                showMessage(errorMessage, "error");
            }
        }
    };

    const updateThrottlingPriority = async () => {
        if (!isThrottlingPrioritiesFetchedSuccessfully) {
            return;
        } else if (throttlingPriority) {
            try {
                const [payload, statusCode] =
                    await updateTenantLinkedThrottlingPriority(
                        envMoniker,
                        tenantId,
                        throttlingPriority
                    );
                if (statusCode != constants.statusCode.NoContent) {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        statusCode
                    );
                    showMessage(errorMessage, "error");
                }
            } catch (error) {
                const errorMessage = `${t(
                    "tenantConfig.d365InformationPage.throttlingPriority.throttlingPriorityUpdateFailed"
                )}.\n${error}`;
                showMessage(errorMessage, "error");
            }
        }
    };

    const handleErpThrottlingPriorityChange = (
        value: IErpThrottlingPriority
    ) => {
        setThrottlingPriorityDropdownItem(value);
        setThrottlingPriority(value);
        setIsUnsavedChangeAvailable(true);
    };

    const handleDismissWarning = () => {
        setIsDismissWarningInProgress(true);
        disableIntegrationUserDomainValidation(
            activeEnvironmentId,
            activeTenantId
        ).then(() => {
            setIsIntegrationUserValid(true);
            setIsDismissWarningInProgress(false);
        });
    };

    const renderDropdownItem = (_: ReactNode, item: ItemDataType<any>) => {
        return (
            <div key={item.value.remoteId}>
                <div>{item.value.remoteId}</div>
                <div>{item.value.name}</div>
            </div>
        );
    };

    const handleSearchBy = (
        keyword: string,
        label: ReactNode,
        item: ItemDataType<any>
    ) => {
        return (
            item.value?.remoteId
                ?.toLowerCase()
                .includes(keyword.toLowerCase()) ||
            label?.toString()?.toLowerCase().includes(keyword.toLowerCase())
        );
    };

    const handleDefaultKeyVaultCompanyChange = (value: Company) => {
        setDefaultKeyVaultCompanyId(value?.remoteId || "");
        setDefaultKeyVaultCompanyDropdownItem(value);
        setIsUnsavedChangeAvailable(true);
    };

    const handleFormSubmit = async () => {
        if (!formRef.current?.check()) {
            const errorMessage = t(
                "tenantConfig.generalConfigurationPage.warning"
            );

            showMessage(errorMessage, "error");

            return;
        }
        showLoader(true);

        await updateAllTenantConfigurations();

        showLoader(false);
        setIsUnsavedChangeAvailable(false);
    };

    const updateAllTenantConfigurations = async () => {
        await updateTenantErpConnectionInfo();

        const promises: any[] = [];
        promises.push();

        await Promise.all(promises)
            .then(() => updateTenantDefaultKeyVaultCompany())
            .then(() => updateThrottlingPriority())
            .then(() =>
                fetchData(
                    envMoniker,
                    activeEnvironmentId,
                    tenantMoniker,
                    activeTenantId
                )
            );
    };

    return (
        (isFetched && (
            <SignalRTenantFeatureSetting
                tenantId={tenantId}
                updateTenantFeatureSettingWorkFlows={(
                    settingWorkflow: ITenantFeatureSettingWorkFlow
                ) => {
                    checkWorkflowProgressAndUpdateWorkflow(settingWorkflow);
                }}
            >
                <div className={"general-configurations__container"}>
                    <div className="general-configurations__content">
                        <Heading
                            level={1}
                            className={"general-configurations__page-header"}
                        >
                            {title}
                        </Heading>

                        {!isIntegrationUserValid &&
                            authMethod?.authenticationType ===
                                AuthenticationType.UsernamePassword && (
                                <Message
                                    type="warning"
                                    className={
                                        "general-configurations__warning-message"
                                    }
                                    showIcon
                                >
                                    {
                                        <>
                                            <p>
                                                {t(
                                                    "tenantConfig.d365InformationPage.integrationUserWrongDomainMessage"
                                                )}
                                            </p>
                                            <div className="general-configuration__warning-message-button">
                                                <Button
                                                    loading={
                                                        isDismissWarningInProgress
                                                    }
                                                    appearance="ghost"
                                                    onClick={
                                                        handleDismissWarning
                                                    }
                                                >
                                                    {t(
                                                        "tenantConfig.d365InformationPage.warningMessageDismissButton"
                                                    )}
                                                </Button>
                                            </div>
                                        </>
                                    }
                                </Message>
                            )}
                        <div>
                            <Form
                                model={formModel}
                                ref={formRef}
                                formValue={formValue}
                                className={
                                    "general-configurations__form--margin"
                                }
                            >
                                <div className={"general-configurations__form"}>
                                    <Form.Group
                                        className={
                                            "general-configurations__grid-item"
                                        }
                                    >
                                        <Form.ControlLabel
                                            className={
                                                "general-configurations__label"
                                            }
                                        >
                                            {t(
                                                "tenantConfig.d365InformationPage.defaultKeyVaultCompany.title"
                                            )}
                                            <Form.HelpText
                                                className={
                                                    "general-configurations__help"
                                                }
                                                tooltip
                                            >
                                                {t(
                                                    "tenantConfig.d365InformationPage.defaultKeyVaultCompany.description"
                                                )}
                                            </Form.HelpText>
                                        </Form.ControlLabel>
                                        <Form.Control
                                            disabled={
                                                isCompaniesDropdownDisabled
                                            }
                                            accepter={InputPicker}
                                            data={companiesData}
                                            renderMenuItem={renderDropdownItem}
                                            virtualized={false}
                                            searchBy={handleSearchBy}
                                            name={"defaultKeyVaultCompany"}
                                            placeholder={t(
                                                "tenantConfig.d365InformationPage.defaultKeyVaultCompany.placeholder"
                                            )}
                                            className={cn(
                                                "general-configurations__form-control",
                                                "general-configurations__company-input-picker",
                                                "vertically-aligned-picker"
                                            )}
                                            onChange={
                                                handleDefaultKeyVaultCompanyChange
                                            }
                                        />
                                    </Form.Group>
                                    {customerType === CustomerType.D365 && (
                                        <Form.Group
                                            className={
                                                "general-configurations__grid-item"
                                            }
                                        >
                                            <Form.ControlLabel
                                                className={
                                                    "general-configurations__label"
                                                }
                                            >
                                                {t(
                                                    "tenantConfig.d365InformationPage.throttlingPriority.title"
                                                )}
                                                <Form.HelpText
                                                    className={
                                                        "general-configurations__help"
                                                    }
                                                    tooltip
                                                >
                                                    {t(
                                                        "tenantConfig.d365InformationPage.throttlingPriority.description"
                                                    )}
                                                </Form.HelpText>
                                            </Form.ControlLabel>
                                            <Form.Control
                                                disabled={
                                                    isThrottlingPriorityDropdownDisabled
                                                }
                                                accepter={InputPicker}
                                                cleanable={false}
                                                searchable={false}
                                                data={throttlingPrioritiesData}
                                                virtualized={false}
                                                name={"throttlingPriority"}
                                                placeholder={t(
                                                    "tenantConfig.d365InformationPage.throttlingPriority.placeholder"
                                                )}
                                                className={cn(
                                                    "general-configurations__form-control",
                                                    "general-configurations__company-input-picker",
                                                    "vertically-aligned-picker"
                                                )}
                                                onChange={
                                                    handleErpThrottlingPriorityChange
                                                }
                                            />
                                        </Form.Group>
                                    )}

                                    <ErpConnectionInfoContainer
                                        authMethods={authMethods}
                                        erpConnectionInfo={erpConnectionInfo}
                                        authenticationMethod={authMethod}
                                        customerType={customerType}
                                        formRef={formRef}
                                        setConnectionInfoChanged={
                                            setConnectionInfoChanged
                                        }
                                        setErpConnectionInfo={
                                            setErpConnectionInfo
                                        }
                                        setAuthenticationMethod={setAuthMethod}
                                    />

                                    <ClientSecretFormGroup
                                        erpInfo={erpSecretsInfo}
                                        environmentMoniker={envMoniker}
                                        tenantMoniker={tenantMoniker}
                                        keyVaultAppRegistrationSecretRotationWorkflow={
                                            keyVaultAppRegistrationSecretRotationWorkflow
                                        }
                                        setIsTenantFetched={setIsTenantFetched}
                                    />
                                    <ResetD365Parameters
                                        envMoniker={envMoniker}
                                        tenantId={tenantId}
                                    />
                                </div>
                            </Form>
                        </div>
                    </div>
                    <Footer onSubmit={handleFormSubmit} />
                </div>
            </SignalRTenantFeatureSetting>
        )) ||
        null
    );
};

export default ErpConfigurations;
